/**
 * Hook interno del módulo CallCenterActiveCallRightColumn — busca lead en
 * `crm_leads` que matchee el teléfono remoto de la llamada activa.
 *
 * Estrategia: ILIKE por últimos 9 dígitos (rápido) + match exacto con
 * `phonesMatch` (lleva en cuenta diferencias de prefijo +/00/sin).
 *
 * Extraído del componente padre para bajar complejidad ciclomática.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { phonesMatch } from '@/lib/crmPhoneMatch';

export interface LeadMatch {
  id: string;
  name: string | null;
  phone: string | null;
}

export function useLeadByPhone(tenantId: string, remotePhone: string | null): LeadMatch | null {
  const [lead, setLead] = useState<LeadMatch | null>(null);

  useEffect(() => {
    const raw = remotePhone?.trim();
    if (!tenantId || !raw) {
      setLead(null);
      return;
    }
    let cancelled = false;
    const last9 = raw.replace(/\D/g, '').slice(-9);

    (async () => {
      const { data, error } = await supabase
        .from('crm_leads')
        .select('id, name, phone')
        .eq('tenant_id', tenantId)
        .ilike('phone', `%${last9}%`)
        .limit(15);
      if (cancelled) return;
      if (error || !data?.length) {
        setLead(null);
        return;
      }
      const rows = data as LeadMatch[];
      setLead(rows.find((r) => phonesMatch(r.phone, raw)) ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, remotePhone]);

  return lead;
}
